/** Pièces validées — copier le lien client (en attendant l’envoi mail). */
import { useEffect, useState } from "react";
import { facturxFetch } from "../../api/client";
import { Button } from "../../components/Button";
import { formatCents, formatDay } from "../../formats/display";
import { formatsFromCompany } from "../../formats/types";
import type { Company } from "../../types";
import "../clients/tables.css";

type LinkDoc = {
  id: number;
  doc_type: string;
  number: string | null;
  issue_date: string | null;
  total_ttc_cents: number;
  client_name: string;
};

function typeLabel(type: string): string {
  if (type === "quote") return "Devis";
  if (type === "credit_note") return "Avoir";
  return "Facture";
}

type PortalLinksProps = {
  company: Company;
  enabled: boolean;
  owner: boolean;
};

export function PortalLinks({ company, enabled, owner }: PortalLinksProps) {
  const [rows, setRows] = useState<LinkDoc[]>([]);
  const [busyId, setBusyId] = useState(0);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(0);
  const fmt = formatsFromCompany(company);

  useEffect(() => {
    if (!company.id) return;
    facturxFetch<{ documents: LinkDoc[] }>(`portal-link.php?company_id=${company.id}`)
      .then((data) => setRows(data.documents || []))
      .catch(() => setRows([]));
  }, [company.id]);

  async function copyLink(id: number) {
    setBusyId(id);
    setErr("");
    try {
      const data = await facturxFetch<{ url: string }>("portal-link.php", {
        method: "POST",
        body: JSON.stringify({ company_id: company.id, document_id: id }),
      });
      try {
        await navigator.clipboard.writeText(data.url);
      } catch {
        window.prompt("Lien", data.url);
      }
      setCopied(id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusyId(0);
    }
  }

  return (
    <div className="fx-fmt-row fx-fmt-row--top">
      <span>Liens</span>
      <div className="flex flex-col gap-2">
        <p className="fx-co-form__hint m-0">Copie un lien pour tester. Un nouveau lien remplace l’ancien pour cette pièce.</p>
        {rows.length === 0 ? (
          <p className="fx-co-form__hint m-0">Aucune pièce validée pour l’instant.</p>
        ) : (
          <table className="fx-data-table text-left text-sm">
            <thead>
              <tr>
                <th>Client</th>
                <th>Pièce</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.client_name}</td>
                  <td>
                    {typeLabel(r.doc_type)} {r.number || "—"} · {formatCents(r.total_ttc_cents, fmt)}
                  </td>
                  <td>{formatDay(r.issue_date, fmt)}</td>
                  <td>
                    <Button
                      variant="ghost"
                      disabled={!owner || !enabled || busyId === r.id}
                      onClick={() => void copyLink(r.id)}
                    >
                      {copied === r.id ? "Copié" : "Copier le lien"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {err ? <p className="fx-co-form__err">{err}</p> : null}
      </div>
    </div>
  );
}
